'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronRight } from 'lucide-react'
import { useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'

interface EmailSpotlightProps {
	placeholder?: string
	buttonText?: string
	onSubmit?: (email: string) => void
	isSubmitting?: boolean
	isSuccess?: boolean
	className?: string
}

export function EmailSpotlight({
	placeholder = 'Blijf op de hoogte',
	buttonText = 'Aanmelden',
	onSubmit,
	isSubmitting = false,
	isSuccess = false,
	className,
}: EmailSpotlightProps) {
	const [email, setEmail] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		if (email && onSubmit) {
			onSubmit(email)
		}
	}

	return (
		<form onSubmit={handleSubmit} className={cn('w-full max-w-lg', className)}>
			<div className="flex items-center gap-3">
				<div className="h-14 flex-1 flex items-center rounded-full bg-white/80 backdrop-blur-sm px-6">
					<div className="flex-1 relative">
						{!email && (
							<p className="absolute text-black/50 pointer-events-none whitespace-nowrap">
								{placeholder}
							</p>
						)}
						<input
							ref={inputRef}
							type="email"
							name="email"
							required
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="w-full bg-transparent outline-none border-none text-black"
						/>
					</div>
				</div>

				<AnimatePresence mode="wait">
					{isSuccess ? (
						<motion.button
							key="success"
							type="button"
							disabled
							className="h-14 px-8 font-semibold rounded-full whitespace-nowrap flex-shrink-0 flex items-center justify-center overflow-hidden bg-white/80 backdrop-blur-sm"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<motion.span
								className="relative flex items-center gap-2 text-black"
								initial={{ y: -50 }}
								animate={{ y: 0 }}
							>
								<Check className="size-4" />
								Ingeschreven
							</motion.span>
						</motion.button>
					) : (
						<motion.button
							key="submit"
							type="submit"
							disabled={isSubmitting}
							className="h-14 px-8 font-semibold text-white rounded-full transition-transform hover:scale-105 active:scale-95 disabled:opacity-70 whitespace-nowrap flex-shrink-0 flex items-center justify-center overflow-hidden"
							style={{
								background: 'linear-gradient(135deg, #f6921e 0%, #e91e8c 100%)',
							}}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<motion.span
								className="relative flex items-center gap-1 group"
								initial={{ x: 0 }}
								exit={{ x: 50, transition: { duration: 0.1 } }}
							>
								{isSubmitting ? '...' : buttonText}
								{!isSubmitting && (
									<ChevronRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
								)}
							</motion.span>
						</motion.button>
					)}
				</AnimatePresence>
			</div>
		</form>
	)
}
